// InstrumentWorkspace.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from './api';

/** =========================
 * Helpers
 * ========================= */
function clsx(...xs) { return xs.filter(Boolean).join(' '); }
function safe(v, fallback = '—') { return v === null || v === undefined || v === '' ? fallback : v; }
function formatDT(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString();
}
function isObject(x) {
  return x && typeof x === 'object' && !Array.isArray(x);
}

/** ---------- DECA helpers ---------- */
function isDECAInstrumentCode(code) {
  const c = String(code || '');
  return c.startsWith('DECA_O_') || c.startsWith('DECA_');
}
function decaDomainsFor(code) {
  const c = String(code || '');
  if (c.includes('INFANT')) return ['IN', 'AR', 'TPF', 'BC'];
  if (c.includes('TODDLER')) return ['IN', 'AR', 'SR', 'TPF', 'BC'];
  return ['IN', 'AR', 'TPF', 'BC'];
}
function decaDomainLabel(dom) {
  if (dom === 'IN') return 'Initiative (IN)';
  if (dom === 'AR') return 'Attachment/Relationships (A/R)';
  if (dom === 'SR') return 'Self-Regulation (SR)';
  if (dom === 'TPF') return 'Total Protective Factors (TPF)';
  if (dom === 'BC') return 'Behavioral Concerns (BC)';
  return dom;
}
function classifyDecaT(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  if (n >= 60) return 'strength';
  if (n >= 41) return 'typical';
  return 'need';
}

/**
 * Normalize options to array of { value, label }.
 * Supports:
 *  - ["A","B"]
 *  - [{value:"A",label:"A"}, ...]
 *  - [{id:"A",name:"A"}] (best effort)
 *  - {"A":"Label A","B":"Label B"}
 */
function normalizeOptions(optionsRaw, itemType) {
  const fallback = (type) => {
    if (type === 'yesno') return [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];
    if (type === 'likert3') return ['Never', 'Sometimes', 'Often'].map((x) => ({ value: x, label: x }));
    if (type === 'likert5') return ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'].map((x) => ({ value: x, label: x }));
    return [];
  };

  if (optionsRaw == null) return fallback(itemType);

  let o = optionsRaw;
  if (typeof o === 'string') {
    const s = o.trim();
    if (!s) return fallback(itemType);
    try { o = JSON.parse(s); } catch { return [{ value: s, label: s }]; }
  }

  if (isObject(o)) {
    const entries = Object.entries(o);
    if (!entries.length) return fallback(itemType);
    return entries.map(([k, v]) => ({
      value: String(k),
      label: v == null ? String(k) : String(v),
    }));
  }

  if (Array.isArray(o)) {
    if (!o.length) return fallback(itemType);
    if (o.every((x) => typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean')) {
      return o.map((x) => ({ value: String(x), label: String(x) }));
    }
    return o.map((x, idx) => {
      if (!isObject(x)) return { value: String(x), label: String(x) };
      const value =
        x.value ?? x.id ?? x.code ?? x.key ?? x.name ?? x.label ?? String(idx);
      const label =
        x.label ?? x.name ?? x.title ?? x.text ?? String(value);
      return { value: String(value), label: String(label) };
    });
  }

  return [{ value: String(o), label: String(o) }];
}

function getAnswerValue(itemType, ans) {
  if (!ans) return null;

  if (itemType === 'number') return ans.value_num != null ? String(ans.value_num) : '';
  if (itemType === 'date') {
    const v = ans.value_text || ans.value_choice || '';
    return v ? String(v).slice(0, 10) : '';
  }
  if (itemType === 'checkbox') {
    if (ans.value_json != null) {
      try {
        const parsed = typeof ans.value_json === 'string' ? JSON.parse(ans.value_json) : ans.value_json;
        if (Array.isArray(parsed)) return parsed.map((x) => String(isObject(x) ? (x.value ?? x.id ?? x.code ?? x.name ?? x.label ?? '') : x)).filter(Boolean);
      } catch {}
    }
    if (ans.value_choice != null) return [String(ans.value_choice)];
    return [];
  }
  if (['select', 'yesno', 'likert3', 'likert5'].includes(itemType)) {
    return ans.value_choice != null ? String(ans.value_choice) : '';
  }
  return ans.value_text != null ? String(ans.value_text) : '';
}

function buildAnswerPayload(item, value) {
  const t = item.item_type;
  const payload = { item_id: item.id, value_choice: null, value_num: null, value_text: null, value_json: null };

  if (t === 'number') {
    payload.value_num = value === '' || value == null ? null : Number(value);
    return payload;
  }
  if (t === 'date') {
    payload.value_text = value ? String(value).slice(0, 10) : null;
    return payload;
  }
  if (t === 'checkbox') {
    payload.value_json = Array.isArray(value) ? value : [];
    return payload;
  }
  if (['select', 'yesno', 'likert3', 'likert5'].includes(t)) {
    payload.value_choice = value ? String(value) : null;
    return payload;
  }
  payload.value_text = value != null ? String(value) : null;
  return payload;
}

function getSubmissionIdFallbackFromPath() {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const idx = parts.findIndex((x) => x === 'instrument-submissions');
    if (idx >= 0 && parts[idx + 1]) return Number(parts[idx + 1]);
    const idx2 = parts.findIndex((x) => x === 'instrument-submissions');
    if (idx2 >= 0 && parts[idx2 + 1]) return Number(parts[idx2 + 1]);
    return null;
  } catch {
    return null;
  }
}

/** =========================
 * UI atoms
 * ========================= */
function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

// ✅ fixed: secondary now has explicit text color (so it won't become "white on white" inside a dark header)
function Button({ children, onClick, variant = 'primary', disabled = false, className = '' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

function SectionStatusBadge({ status }) {
  const s = String(status || 'draft');
  if (s === 'submitted') return <Badge tone="green">submitted</Badge>;
  if (s === 'locked') return <Badge tone="purple">locked</Badge>;
  return <Badge tone="amber">draft</Badge>;
}

function ScoreBadge({ cls }) {
  if (!cls) return <Badge>—</Badge>;
  if (cls === 'strength') return <Badge tone="green">strength</Badge>;
  if (cls === 'typical') return <Badge tone="blue">typical</Badge>;
  return <Badge tone="red">need</Badge>;
}

/** =========================
 * Component
 * ========================= */
export default function InstrumentWorkspace({ submissionId: submissionIdProp }) {
  const params = useParams();
  const paramId = params?.id ? Number(params.id) : null;

  const submissionId = useMemo(() => {
    const v = Number(submissionIdProp || paramId || getSubmissionIdFallbackFromPath() || 0);
    return Number.isFinite(v) ? v : 0;
  }, [submissionIdProp, paramId]);

  const [loading, setLoading] = useState(false);

  const [submission, setSubmission] = useState(null);
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sectionState, setSectionState] = useState([]);
  const [scores, setScores] = useState([]);

  const [activeSectionId, setActiveSectionId] = useState(null);

  const [saveBusy, setSaveBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);

  const [msg, setMsg] = useState(null);
  const msgTimerRef = useRef(null);

  const [draftValues, setDraftValues] = useState({});
  const [dirtyItemIds, setDirtyItemIds] = useState(new Set());

  // shell_only manual scores
  const [manualScores, setManualScores] = useState({});
  const [manualBusy, setManualBusy] = useState(false);
  const [decaComputeBusy, setDecaComputeBusy] = useState(false);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const role = user?.role || null;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isParent = role === 'parent';

  function setFlash(text, tone = 'info') {
    setMsg({ text, tone });
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    msgTimerRef.current = setTimeout(() => setMsg(null), 4500);
  }

  const sectionStatusById = useMemo(() => {
    const map = {};
    for (const st of sectionState || []) map[String(st.section_id)] = st.status;
    return map;
  }, [sectionState]);

  const itemsBySectionId = useMemo(() => {
    const m = new Map();
    for (const it of items || []) {
      const sid = String(it.section_id);
      if (!m.has(sid)) m.set(sid, []);
      m.get(sid).push(it);
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id - b.id));
      m.set(k, arr);
    }
    return m;
  }, [items]);

  const activeSection = useMemo(
    () => sections.find((s) => Number(s.id) === Number(activeSectionId)) || null,
    [sections, activeSectionId]
  );

  const isShellOnly = useMemo(() => {
    const distribution = submission?.distribution_mode || submission?.definition_mode || 'full_content';
    return String(distribution) === 'shell_only';
  }, [submission]);

  const isDeca = useMemo(() => isDECAInstrumentCode(submission?.instrument_code), [submission]);

  const totalScore = useMemo(() => {
    return (scores || []).find((x) => String(x.domain_code || '') === 'TOTAL') ||
           (scores || []).find((x) => x.section_id == null) || null;
  }, [scores]);

  const scoresBySectionId = useMemo(() => {
    const map = {};
    for (const sc of scores || []) {
      if (sc.section_id == null) continue;
      map[String(sc.section_id)] = sc;
    }
    return map;
  }, [scores]);

  function initActiveSection() {
    if (!sections.length) return;
    if (activeSectionId && sections.some((s) => Number(s.id) === Number(activeSectionId))) return;
    setActiveSectionId(sections[0].id);
  }

  function hydrateDraftForSection(sectionId) {
    const arr = itemsBySectionId.get(String(sectionId)) || [];
    const next = {};
    for (const it of arr) {
      const ans = answers[String(it.id)];
      next[String(it.id)] = getAnswerValue(it.item_type, ans);
      if (it.item_type === 'checkbox' && !Array.isArray(next[String(it.id)])) next[String(it.id)] = [];
    }
    setDraftValues(next);
    setDirtyItemIds(new Set());
  }

  async function loadWorkspace() {
    if (!submissionId) {
      setFlash('Missing submission id in URL', 'error');
      return;
    }
    setLoading(true);
    try {
      const data = await apiGet(`/instrument-submissions/${submissionId}`);
      setSubmission(data.submission || null);
      setSections(data.sections || []);
      setItems(data.items || []);
      setAnswers(data.answers || {});
      setSectionState(data.section_state || []);
      setScores(data.scores || []);
      const firstId = (data.sections || [])?.[0]?.id || null;
      setActiveSectionId((prev) => prev || firstId);
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Failed to load submission', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadWorkspace(); }, [submissionId]); // eslint-disable-line
  useEffect(() => { initActiveSection(); }, [sections]); // eslint-disable-line
  useEffect(() => { if (activeSectionId) hydrateDraftForSection(activeSectionId); }, [activeSectionId, itemsBySectionId, answers]); // eslint-disable-line

  // hydrate manualScores by domain_code (for shell_only)
  useEffect(() => {
    if (!isShellOnly) return;
    const ms = {};
    for (const sc of scores || []) {
      const key = sc.domain_code || (sc.section_id == null ? 'TOTAL' : String(sc.section_id));
      ms[key] = {
        domain_code: sc.domain_code || null,
        section_id: sc.section_id == null ? null : Number(sc.section_id),
        raw_points: sc.raw_points ?? null,
        max_points: sc.max_points ?? null,
        pct: sc.pct ?? null,
        t_score: sc.t_score ?? null,
        percentile: sc.percentile ?? null,
        classification: sc.classification ?? null,
      };
    }
    setManualScores(ms);
  }, [isShellOnly, scores]);

  function sectionEditable(sectionId) {
    const subStatus = submission?.status || 'draft';
    if (subStatus === 'locked') return false;
    const st = sectionStatusById[String(sectionId)] || 'draft';
    if (st !== 'draft') return false;
    return true;
  }

  function submissionEditable() {
    return (submission?.status || 'draft') === 'draft';
  }

  function onChangeItem(item, value) {
    const id = String(item.id);
    setDraftValues((p) => ({ ...p, [id]: value }));
    setDirtyItemIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  const activeSectionItems = useMemo(() => {
    if (!activeSectionId) return [];
    return itemsBySectionId.get(String(activeSectionId)) || [];
  }, [activeSectionId, itemsBySectionId]);

  const activeDirtyCount = useMemo(() => dirtyItemIds.size, [dirtyItemIds]);

  async function saveActiveSection() {
    if (!activeSectionId) return;
    if (!submissionEditable()) return setFlash('Submission is not editable', 'error');
    if (!sectionEditable(activeSectionId)) return setFlash('Section is not editable', 'error');

    const dirty = Array.from(dirtyItemIds);
    if (!dirty.length) return setFlash('Nothing to save', 'info');

    const payloadAnswers = [];
    for (const it of activeSectionItems) {
      const id = String(it.id);
      if (!dirtyItemIds.has(id)) continue;
      payloadAnswers.push(buildAnswerPayload(it, draftValues[id]));
    }

    setSaveBusy(true);
    try {
      await apiPut(`/instrument-submissions/${submissionId}/sections/${activeSectionId}/answers`, { answers: payloadAnswers });
      setFlash('Section saved', 'ok');
      await loadWorkspace();
      setActiveSectionId(Number(activeSectionId));
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaveBusy(false);
    }
  }

  async function submitActiveSection() {
    if (!activeSectionId) return;
    if (!submissionEditable()) return setFlash('Submission is not editable', 'error');

    setSubmitBusy(true);
    try {
      await apiPost(`/instrument-submissions/${submissionId}/sections/${activeSectionId}/submit`, {});
      setFlash('Section submitted', 'ok');
      await loadWorkspace();
      setActiveSectionId(Number(activeSectionId));
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      if (data?.error === 'Required items missing' && Array.isArray(data.missing_item_ids)) {
        setFlash(`Required items missing: ${data.missing_item_ids.join(', ')}`, 'error');
      } else {
        setFlash(data?.error || 'Submit section failed', 'error');
      }
    } finally {
      setSubmitBusy(false);
    }
  }

  async function submitAll() {
    if (!submissionEditable()) return setFlash('Submission is not editable', 'error');
    setSubmitBusy(true);
    try {
      const r = await apiPost(`/instrument-submissions/${submissionId}/submit`, {});
      setFlash(`Submission submitted${r?.scores_updated ? ' (scores updated)' : ''}`, 'ok');
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      if (data?.error === 'All sections must be submitted' && Array.isArray(data.not_submitted_section_ids)) {
        setFlash(`Not submitted sections: ${data.not_submitted_section_ids.join(', ')}`, 'error');
      } else {
        setFlash(data?.error || 'Submit all failed', 'error');
      }
    } finally {
      setSubmitBusy(false);
    }
  }

  async function recomputeScores() {
    setSubmitBusy(true);
    try {
      const r = await apiPost(`/instrument-submissions/${submissionId}/recompute-scores`, {});
      setFlash(r?.skipped ? 'Recompute skipped (shell_only)' : 'Scores recomputed', 'ok');
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Recompute failed', 'error');
    } finally {
      setSubmitBusy(false);
    }
  }

  async function lockSubmission() {
    if (!submission) return;
    setLockBusy(true);
    try {
      await apiPost(`/instrument-submissions/${submissionId}/lock`, {});
      setFlash('Submission locked', 'ok');
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Lock failed', 'error');
    } finally {
      setLockBusy(false);
    }
  }
  async function unlockSubmission() {
    if (!submission) return;
    setLockBusy(true);
    try {
      await apiPost(`/instrument-submissions/${submissionId}/unlock`, {});
      setFlash('Submission unlocked (draft)', 'ok');
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Unlock failed', 'error');
    } finally {
      setLockBusy(false);
    }
  }

  // -------- Generic manual editor (fallback for non-DECA shell_only) --------
  const manualSections = useMemo(() => {
    const arr = (sections || []).map((s) => ({
      key: String(s.id),
      section_id: Number(s.id),
      title: s.title,
      code: s.code
    }));
    return [{ key: 'TOTAL', section_id: null, title: 'TOTAL', code: 'TOTAL' }, ...arr];
  }, [sections]);

  function setGenericManual(sectionKey, patch) {
    const section = manualSections.find((x) => x.key === sectionKey);
    const domain_code = sectionKey === 'TOTAL' ? 'TOTAL' : (section?.code || null);

    setManualScores((p) => ({
      ...(p || {}),
      [domain_code || sectionKey]: {
        ...(p?.[domain_code || sectionKey] || {}),
        domain_code: domain_code || null,
        section_id: sectionKey === 'TOTAL' ? null : Number(sectionKey),
        ...patch
      },
    }));
  }

  // saveManualScores: sends domain_code, normalizes TOTAL, skips junk keys
  async function saveManualScores() {
    if (!isShellOnly) return;
    setManualBusy(true);
    try {
      const scoresArr = [];

      for (const [key, row] of Object.entries(manualScores || {})) {
        let domain_code = row.domain_code || key;

        if (!domain_code) continue;
        if (String(domain_code).toLowerCase() === 'total') domain_code = 'TOTAL';

        const isNumericKey = /^[0-9]+$/.test(String(domain_code));
        if (isNumericKey && !row.domain_code) continue;

        const has =
          row.raw_points != null || row.max_points != null || row.pct != null ||
          row.t_score != null || row.percentile != null || row.classification != null;

        if (!has) continue;

        scoresArr.push({
          domain_code,
          section_id: row.section_id ?? null,
          raw_points: row.raw_points == null || row.raw_points === '' ? null : Number(row.raw_points),
          max_points: row.max_points == null || row.max_points === '' ? null : Number(row.max_points),
          pct: row.pct == null || row.pct === '' ? null : Number(row.pct),
          t_score: row.t_score == null || row.t_score === '' ? null : Number(row.t_score),
          percentile: row.percentile == null || row.percentile === '' ? null : Number(row.percentile),
          classification: row.classification || null,
        });
      }

      if (!scoresArr.length) {
        setFlash('Nothing to save', 'info');
        return;
      }

      await apiPost(`/instrument-submissions/${submissionId}/scores/manual`, { scores: scoresArr });
      setFlash('Manual scores saved', 'ok');
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      setFlash(e?.response?.data?.error || 'Failed to save manual scores', 'error');
    } finally {
      setManualBusy(false);
    }
  }

  // Compute autosaves first
  async function computeDecaFromNorms() {
    if (!isShellOnly || !isDeca) return;
    setDecaComputeBusy(true);
    try {
      await saveManualScores();
      const r = await apiPost(`/instrument-submissions/${submissionId}/deca/compute`, {});
      if (r?.missing_norms?.length) {
        setFlash(`Computed, but missing norms for: ${r.missing_norms.map((x) => x.domain).join(', ')}`, 'error');
      } else {
        setFlash('DECA computed from norms', 'ok');
      }
      await loadWorkspace();
    } catch (e) {
      console.error(e);
      const data = e?.response?.data;
      if (data?.missing_domains?.length) {
        setFlash(`Missing raw scores: ${data.missing_domains.join(', ')}`, 'error');
      } else if (data?.missing_norms?.length) {
        setFlash(`Missing norms for: ${data.missing_norms.map((x) => x.domain).join(', ')}`, 'error');
      } else {
        setFlash(data?.error || 'Compute failed', 'error');
      }
    } finally {
      setDecaComputeBusy(false);
    }
  }

  const sectionsForTabs = useMemo(() => {
    return (sections || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id - b.id));
  }, [sections]);

  const headerTitle = useMemo(() => {
    if (!submission) return 'Instrument Workspace';
    return `${submission.instrument_name || submission.instrument_code || 'Instrument'} — ${submission.period}`;
  }, [submission]);

  function renderItemInput(it) {
    const id = String(it.id);
    const v = draftValues[id];
    const label = it.label || it.item_code || `Item #${it.id}`;
    const required = !!it.required;
    const disabled = !submissionEditable() || !sectionEditable(it.section_id);

    const baseLabel = (
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-900">
            {label} {required ? <span className="text-rose-600">*</span> : null}
          </div>
          {it.prompt_text ? <div className="text-xs text-slate-600 mt-0.5">{it.prompt_text}</div> : null}
          {it.help_text ? <div className="text-xs text-slate-500 mt-0.5">{it.help_text}</div> : null}
          <div className="text-[11px] text-slate-400 mt-0.5 font-mono">{it.item_code}</div>
        </div>
        <Badge>{it.item_type}</Badge>
      </div>
    );

    const opts = normalizeOptions(it.options, it.item_type);

    if (it.item_type === 'textarea') {
      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <textarea
            disabled={disabled}
            className="mt-2 w-full px-3 py-2 rounded-xl border min-h-[110px] disabled:bg-slate-50"
            value={v ?? ''}
            onChange={(e) => onChangeItem(it, e.target.value)}
          />
        </div>
      );
    }

    if (it.item_type === 'text') {
      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <input
            disabled={disabled}
            className="mt-2 w-full px-3 py-2 rounded-xl border disabled:bg-slate-50"
            value={v ?? ''}
            onChange={(e) => onChangeItem(it, e.target.value)}
          />
        </div>
      );
    }

    if (it.item_type === 'number') {
      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <input
            disabled={disabled}
            type="number"
            className="mt-2 w-full px-3 py-2 rounded-xl border disabled:bg-slate-50"
            value={v ?? ''}
            onChange={(e) => onChangeItem(it, e.target.value)}
          />
        </div>
      );
    }

    if (it.item_type === 'date') {
      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <input
            disabled={disabled}
            type="date"
            className="mt-2 w-full px-3 py-2 rounded-xl border disabled:bg-slate-50"
            value={v ?? ''}
            onChange={(e) => onChangeItem(it, e.target.value)}
          />
        </div>
      );
    }

    if (['select', 'yesno', 'likert3', 'likert5'].includes(it.item_type)) {
      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <select
            disabled={disabled}
            className="mt-2 w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-50"
            value={v ?? ''}
            onChange={(e) => onChangeItem(it, e.target.value)}
          >
            <option value="">Select…</option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    }

    if (it.item_type === 'checkbox') {
      const arr = Array.isArray(v) ? v.map(String) : [];
      const toggle = (val) => {
        const s = String(val);
        const next = arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s];
        onChangeItem(it, next);
      };

      return (
        <div className="border rounded-2xl p-3 bg-white">
          {baseLabel}
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {opts.length ? (
              opts.map((o) => {
                const checked = arr.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer',
                      checked ? 'bg-sky-50 border-sky-200' : 'bg-white hover:bg-slate-50'
                    )}
                  >
                    <input disabled={disabled} type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
                    <span className="text-sm text-slate-800">{o.label}</span>
                  </label>
                );
              })
            ) : (
              <div className="text-sm text-slate-500">No options defined.</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="border rounded-2xl p-3 bg-white">
        {baseLabel}
        <div className="mt-2 text-sm text-slate-500">Unsupported item type</div>
      </div>
    );
  }

  if (!submissionId) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="font-semibold text-slate-900">Missing submission id</div>
          <div className="text-slate-600 text-sm mt-1">Open with a valid submission id.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* ✅ NEW neutral header (no gradient / consistent across roles) */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">
              {headerTitle}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500">Submission #{submissionId}</span>
              {submission ? (
                <>
                  <Badge tone="blue">{submission.form_kind}</Badge>
                  <Badge tone={isShellOnly ? 'slate' : 'green'}>{isShellOnly ? 'shell_only' : 'full_content'}</Badge>
                  <SectionStatusBadge status={submission.status} />
                  {submission.discipline_code ? <Badge tone="slate">{submission.discipline_code}</Badge> : null}
                  {submission.instrument_type ? <Badge tone="amber">{submission.instrument_type}</Badge> : null}
                  <span className="text-slate-500">
                    started: {formatDT(submission.started_at)}
                    {submission.submitted_at ? ` • submitted: ${formatDT(submission.submitted_at)}` : ''}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={loadWorkspace} disabled={loading}>Reload</Button>

            {isAdmin && submission ? (
              submission.status !== 'locked' ? (
                <Button variant="secondary" onClick={lockSubmission} disabled={lockBusy || loading}>
                  {lockBusy ? 'Locking…' : 'Lock'}
                </Button>
              ) : (
                <Button variant="secondary" onClick={unlockSubmission} disabled={lockBusy || loading}>
                  {lockBusy ? 'Unlocking…' : 'Unlock'}
                </Button>
              )
            ) : null}

            {!isShellOnly ? (
              <Button variant="secondary" onClick={recomputeScores} disabled={submitBusy || loading}>
                Recompute scores
              </Button>
            ) : null}

            <Button variant="primary" onClick={submitAll} disabled={submitBusy || loading || !submissionEditable()}>
              Submit all
            </Button>
          </div>
        </div>

        {msg ? (
          <div
            className={clsx(
              'mt-3 px-3 py-2 rounded-xl text-sm border',
              msg.tone === 'error'
                ? 'bg-rose-50 text-rose-800 border-rose-200'
                : msg.tone === 'ok'
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-slate-50 text-slate-700 border-slate-200'
            )}
          >
            {msg.text}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="font-semibold text-slate-900">Sections</div>
              <div className="text-xs text-slate-500">{sectionsForTabs.length} sections</div>
            </div>

            <div className="p-2 space-y-2">
              {sectionsForTabs.map((s) => {
                const st = sectionStatusById[String(s.id)] || 'draft';
                const isActive = Number(activeSectionId) === Number(s.id);
                const secScore = scoresBySectionId[String(s.id)];
                const inactive = !s.is_active;

                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSectionId(s.id)}
                    className={clsx(
                      'w-full text-left p-3 rounded-xl border transition-colors',
                      isActive ? 'bg-sky-50 border-sky-200' : 'bg-white hover:bg-slate-50 border-slate-200'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-mono text-[12px] text-slate-600">{s.code}</div>
                        <div className={clsx('font-semibold', inactive ? 'text-slate-400' : 'text-slate-900')}>
                          {s.title}
                        </div>
                        {inactive ? <div className="text-xs text-rose-600 mt-0.5">inactive section</div> : null}
                        <div className="text-xs text-slate-500 mt-1">
                          roles: {(s.owner_roles || []).join(', ') || '—'}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <SectionStatusBadge status={st} />
                        {secScore ? (
                          <div className="flex items-center gap-1">
                            <Badge tone="slate">{safe(secScore.pct, '—')}%</Badge>
                            <ScoreBadge cls={secScore.classification} />
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {!sectionsForTabs.length ? (
                <div className="p-4 text-slate-500 text-sm">No sections.</div>
              ) : null}
            </div>
          </div>

          <div className="bg-white border rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">Score summary</div>
              {totalScore ? <ScoreBadge cls={totalScore.classification} /> : <Badge>—</Badge>}
            </div>
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Raw: <span className="font-semibold">{safe(totalScore?.raw_points, 0)}</span></div>
              <div>Max: <span className="font-semibold">{safe(totalScore?.max_points, 0)}</span></div>
              <div>Pct: <span className="font-semibold">{totalScore?.pct != null ? `${Number(totalScore.pct).toFixed(2)}%` : '—'}</span></div>
              <div>T-score: <span className="font-semibold">{totalScore?.t_score != null ? Number(totalScore.t_score).toFixed(2) : '—'}</span></div>
              <div>Percentile: <span className="font-semibold">{totalScore?.percentile != null ? Number(totalScore.percentile).toFixed(2) : '—'}</span></div>
            </div>
          </div>

          {/* Manual scores panel: DECA vs Generic fallback */}
          {isShellOnly ? (
            <div className="bg-white border rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900">
                  {isDeca ? 'DECA manual scores' : 'Manual scores (shell_only)'}
                </div>

                <div className="flex items-center gap-2">
                  {isDeca && !isParent ? (
                    <Button
                      variant="secondary"
                      onClick={computeDecaFromNorms}
                      disabled={decaComputeBusy || manualBusy || !submissionEditable()}
                    >
                      {decaComputeBusy ? 'Computing…' : 'Compute from norms'}
                    </Button>
                  ) : null}

                  {!isParent ? (
                    <Button variant="primary" onClick={saveManualScores} disabled={manualBusy || !submissionEditable()}>
                      {manualBusy ? 'Saving…' : 'Save manual scores'}
                    </Button>
                  ) : (
                    <Badge tone="slate">read-only</Badge>
                  )}
                </div>
              </div>

              {isDeca ? (
                <DecaManualPanel
                  instrumentCode={submission?.instrument_code}
                  manualScores={manualScores}
                  setManualScores={setManualScores}
                  disabled={!submissionEditable() || isParent}
                />
              ) : (
                <GenericShellManualPanel
                  manualSections={manualSections}
                  manualScores={manualScores}
                  setGenericManual={setGenericManual}
                  disabled={!submissionEditable() || isParent}
                />
              )}

              <div className="mt-3 text-xs text-slate-500">
                Manual scores are typically entered by teacher/admin for licensed shell instruments.
              </div>
            </div>
          ) : null}
        </div>

        {/* Right */}
        <div className="lg:col-span-8">
          <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
            <div className="p-3 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{activeSection ? activeSection.title : 'Section'}</div>
                <div className="text-xs text-slate-500">
                  {activeSection ? (
                    <>
                      <span className="font-mono">{activeSection.code}</span> •{' '}
                      <SectionStatusBadge status={sectionStatusById[String(activeSection.id)] || 'draft'} />
                      {!sectionEditable(activeSection.id) ? <span className="ml-2"><Badge tone="purple">read-only</Badge></span> : null}
                    </>
                  ) : '—'}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                <Button
                  variant="secondary"
                  onClick={saveActiveSection}
                  disabled={saveBusy || loading || !submissionEditable() || !sectionEditable(activeSectionId) || activeDirtyCount === 0}
                >
                  {saveBusy ? 'Saving…' : `Save section${activeDirtyCount ? ` (${activeDirtyCount})` : ''}`}
                </Button>
                <Button
                  variant="primary"
                  onClick={submitActiveSection}
                  disabled={submitBusy || loading || !submissionEditable() || !sectionEditable(activeSectionId)}
                >
                  {submitBusy ? 'Submitting…' : 'Submit section'}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-slate-50">
              {loading ? (
                <div className="text-slate-600">Loading…</div>
              ) : !activeSection ? (
                <div className="text-slate-600">No section selected.</div>
              ) : (
                <div className="space-y-3">
                  {activeSectionItems.length ? (
                    activeSectionItems.map((it) => <div key={it.id}>{renderItemInput(it)}</div>)
                  ) : (
                    <div className="text-slate-600 text-sm">No items in this section.</div>
                  )}
                </div>
              )}
            </div>

            <div className="p-3 border-t text-xs text-slate-500 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                {submission ? (
                  <>
                    Updated: <span className="font-mono">{safe(submission.updated_at)}</span>
                    {submission.created_by_role ? ` • created by: ${submission.created_by_role}` : ''}
                  </>
                ) : null}
              </div>
              <div className="text-slate-500">Required validation happens on submit (server-side).</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-slate-500">
        Debug: submission_id={submissionId} • role={role || 'unknown'} • mode={isShellOnly ? 'shell_only' : 'full_content'}
      </div>
    </div>
  );
}

/** =========================
 * Generic Shell Manual Panel (fallback for non-DECA)
 * ========================= */
function GenericShellManualPanel({ manualSections, manualScores, setGenericManual, disabled }) {
  return (
    <div className="mt-3 space-y-3">
      {manualSections.map((s) => {
        const domainKey = s.key === 'TOTAL' ? 'TOTAL' : (s.code || s.key);
        const row = manualScores?.[domainKey] || {};

        return (
          <div key={s.key} className="border rounded-2xl p-3 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{s.title}</div>
                <div className="text-xs text-slate-500 font-mono">{s.code}</div>
              </div>
              <ScoreBadge cls={row.classification} />
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              {['raw_points', 'max_points', 'pct', 't_score', 'percentile'].map((k) => (
                <div key={k}>
                  <label className="text-xs text-slate-600">{k.replace('_', ' ')}</label>
                  <input
                    disabled={disabled}
                    type="number"
                    className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                    value={row[k] ?? ''}
                    onChange={(e) => setGenericManual(s.key, { [k]: e.target.value })}
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-slate-600">classification</label>
                <select
                  disabled={disabled}
                  className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                  value={row.classification || ''}
                  onChange={(e) => setGenericManual(s.key, { classification: e.target.value || null })}
                >
                  <option value="">—</option>
                  <option value="strength">strength</option>
                  <option value="typical">typical</option>
                  <option value="need">need</option>
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** =========================
 * DECA Manual Panel
 * ========================= */
function DecaManualPanel({ instrumentCode, manualScores, setManualScores, disabled }) {
  const domains = useMemo(() => decaDomainsFor(instrumentCode), [instrumentCode]);

  const getRow = (k) => manualScores?.[k] || {};

  function setRow(domain, patch) {
    setManualScores((p) => ({
      ...(p || {}),
      [domain]: { ...(p?.[domain] || {}), domain_code: domain, section_id: null, ...patch },
    }));
  }

  // Auto-calc: TPF raw_points = sum of T-scores; auto class from T
  useEffect(() => {
    const needTPF = domains.includes('TPF');
    if (!needTPF) return;

    const tIN = Number(getRow('IN')?.t_score);
    const tAR = Number(getRow('AR')?.t_score);
    const tSR = Number(getRow('SR')?.t_score);

    const parts = [];
    if (Number.isFinite(tIN)) parts.push(tIN);
    if (Number.isFinite(tAR)) parts.push(tAR);
    if (domains.includes('SR') && Number.isFinite(tSR)) parts.push(tSR);

    if (!parts.length) return;

    const sumT = parts.reduce((a, b) => a + b, 0);

    const cur = getRow('TPF')?.raw_points;
    const curNum = cur == null || cur === '' ? null : Number(cur);
    if (curNum == null || Math.abs(curNum - sumT) > 0.0001) {
      setRow('TPF', { raw_points: sumT });
    }

    const tpfT = Number(getRow('TPF')?.t_score);
    if (Number.isFinite(tpfT)) {
      const cls = getRow('TPF')?.classification;
      const next = classifyDecaT(tpfT);
      if (!cls || cls !== next) setRow('TPF', { classification: next });
    }
  }, [domains, manualScores]); // eslint-disable-line

  function onTChange(domain, v) {
    const n = v === '' ? null : Number(v);
    setRow(domain, { t_score: v === '' ? null : n, classification: n == null ? null : classifyDecaT(n) });
  }

  function onNum(domain, key, v) {
    setRow(domain, { [key]: v === '' ? null : Number(v) });
  }

  function onClass(domain, v) {
    setRow(domain, { classification: v || null });
  }

  return (
    <div className="mt-3 space-y-3">
      {domains.map((dom) => {
        const row = getRow(dom);
        const isTPF = dom === 'TPF';

        return (
          <div key={dom} className="border rounded-2xl p-3 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{decaDomainLabel(dom)}</div>
                <div className="text-xs text-slate-500 font-mono">{dom}</div>
              </div>
              <ScoreBadge cls={row.classification} />
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-600">
                  {isTPF ? 'sum T (auto)' : 'raw score'}
                </label>
                <input
                  disabled={disabled || isTPF} // ✅ TPF raw is read-only (auto)
                  type="number"
                  className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                  value={row.raw_points ?? ''}
                  onChange={(e) => onNum(dom, 'raw_points', e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-slate-600">t_score</label>
                <input
                  disabled={disabled}
                  type="number"
                  className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                  value={row.t_score ?? ''}
                  onChange={(e) => onTChange(dom, e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-slate-600">percentile</label>
                <input
                  disabled={disabled}
                  type="number"
                  className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                  value={row.percentile ?? ''}
                  onChange={(e) => onNum(dom, 'percentile', e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-slate-600">classification</label>
                <select
                  disabled={disabled}
                  className="w-full px-3 py-2 rounded-xl border bg-white disabled:bg-slate-100"
                  value={row.classification || ''}
                  onChange={(e) => onClass(dom, e.target.value)}
                >
                  <option value="">—</option>
                  <option value="strength">strength</option>
                  <option value="typical">typical</option>
                  <option value="need">need</option>
                </select>
              </div>
            </div>

            <div className="mt-2 text-[11px] text-slate-500">
              DECA classification uses T-score: strength ≥60, typical 41–59, need ≤40.
            </div>
          </div>
        );
      })}
    </div>
  );
}



